/**
 * W-080 — CMS pages editor. Stories: US-A09, US-913.
 *
 * AC1 an editor for the Arabic and French versions of each page
 * AC2 the change appears in the app on the next open
 * AC4 publishing Privacy or Terms records a new version with an effective date
 *     → WD-9
 *
 * TODO(US-A09): "rich-text editor" in the story; docs/03 §4.2 stores
 * `cms_pages.body_ar/body_fr` as plain `text` and the mobile app renders it.
 * Markdown is the working assumption — it survives a `text` column, renders
 * identically in both directions, and needs no editor dependency. If the client
 * wants formatting toolbars, the column stays and the editor is swapped here.
 */
import { useInvalidate, useList, useUpdate } from '@refinedev/core'
import { Alert, Button, Card, Input, Space, Tabs, Typography } from 'antd'
import dayjs from 'dayjs'
import { useEffect, useState } from 'react'
import { PublishCmsVersionDialog } from '@/components/dialogs'
import { recordAudit } from '@/lib/audit'
import { useAdminTranslate } from '@/providers/i18n-provider'
import type { CmsPage, CmsPageKey } from '@/types'

const PAGE_KEYS: CmsPageKey[] = ['about', 'privacy', 'terms']

/** Only these two carry a legally meaningful version (US-A09 AC4). */
const VERSIONED: CmsPageKey[] = ['privacy', 'terms']

export const CmsPagesEditorPage = () => {
  const translate = useAdminTranslate()
  const { data, isLoading } = useList<CmsPage>({
    resource: 'cms_pages',
    pagination: { mode: 'off' },
  })
  const pageLabel = (key: CmsPageKey) => translate(`admin.screens.cms.${key}`)
  const { mutate: update } = useUpdate()
  const invalidate = useInvalidate()

  const [activeKey, setActiveKey] = useState<CmsPageKey>('about')
  const [bodyAr, setBodyAr] = useState('')
  const [bodyFr, setBodyFr] = useState('')
  const [publishOpen, setPublishOpen] = useState(false)

  const pages = data?.data ?? []
  const page = pages.find(candidate => candidate.key === activeKey)

  useEffect(() => {
    setBodyAr(page?.body_ar ?? '')
    setBodyFr(page?.body_fr ?? '')
  }, [page?.key, page?.body_ar, page?.body_fr])

  const save = (extra: Partial<CmsPage> = {}) => {
    update(
      {
        resource: 'cms_pages',
        id: activeKey,
        values: { body_ar: bodyAr, body_fr: bodyFr, ...extra },
      },
      { onSuccess: () => void invalidate({ resource: 'cms_pages', invalidates: ['list'] }) },
    )
  }

  return (
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      <Typography.Title level={3} style={{ marginBottom: 0 }}>
        {translate('admin.pages.pages')}
      </Typography.Title>

      <Tabs
        activeKey={activeKey}
        onChange={key => setActiveKey(key as CmsPageKey)}
        items={PAGE_KEYS.map(key => ({ key, label: pageLabel(key) }))}
      />

      <Alert
        type="info"
        showIcon
        message={translate('admin.screens.cms.markdownTitle')}
        description={translate('admin.screens.cms.markdownBody')}
      />

      <Card
        size="small"
        loading={isLoading}
        title={translate('admin.screens.cms.cardTitle', {
          page: pageLabel(activeKey),
          version: page?.version ?? '—',
        })}
        extra={
          <Typography.Text type="secondary">
            {translate('admin.screens.cms.updatedAt', {
              date: page ? dayjs(page.updated_at).format('DD/MM/YYYY') : '—',
            })}
          </Typography.Text>
        }
      >
        <Space direction="vertical" style={{ width: '100%' }} size="middle">
          <div>
            <Typography.Text strong>{translate('common.languageArabic')}</Typography.Text>
            <Input.TextArea
              dir="rtl"
              rows={14}
              value={bodyAr}
              onChange={event => setBodyAr(event.target.value)}
            />
          </div>
          <div>
            <Typography.Text strong>{translate('common.languageFrench')}</Typography.Text>
            <Input.TextArea
              rows={14}
              value={bodyFr}
              onChange={event => setBodyFr(event.target.value)}
            />
          </div>

          <Space>
            <Button type="primary" onClick={() => save()}>
              {translate('admin.screens.cms.save')}
            </Button>
            {VERSIONED.includes(activeKey) ? (
              <Button onClick={() => setPublishOpen(true)}>
                {translate('admin.screens.cms.publishVersion')}
              </Button>
            ) : null}
          </Space>
        </Space>
      </Card>

      {/* WD-9 */}
      <PublishCmsVersionDialog
        open={publishOpen}
        pageLabel={pageLabel(activeKey)}
        currentVersion={page?.version ?? ''}
        onCancel={() => setPublishOpen(false)}
        onConfirm={async ({ version, effectiveDate }) => {
          save({ version, effective_date: effectiveDate })
          await recordAudit({
            action: 'cms.publish',
            target: 'cms_pages',
            targetId: activeKey,
            before: { version: page?.version },
            after: { version, effective_date: effectiveDate },
          })
          setPublishOpen(false)
        }}
      />
    </Space>
  )
}
